import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { Activity, Pause, TrendingUp, X } from 'lucide-react';
import { slugify } from '../utils/urlUtils';

const TIMEDATA_BASE = process.env.NODE_ENV === 'production'
    ? 'https://timedata.ukpetitiontracker.com'
    : 'http://localhost:5001';

// A sync lands every 10 minutes and moves ~90 petitions at once, so the batch
// is paced out rather than dumped. Every figure is a real measured change
// between consecutive snapshots; only the pacing is ours.
// Signatures do not arrive on a metronome, and a fixed cadence read as a
// carousel rather than a feed. Each reveal waits a random interval in this
// range, so the rhythm is uneven the way real arrivals are.
const REVEAL_MIN_MS = 900;
const REVEAL_MAX_MS = 3500;
const nextRevealDelay = () =>
    REVEAL_MIN_MS + Math.random() * (REVEAL_MAX_MS - REVEAL_MIN_MS);
const REFETCH_MS = 3 * 60 * 1000;
const APPEAR_DELAY = 2500;      // overlay mode: let the page settle first
const APPEAR_DELAY_CARD = 0;   // card mode: it is part of the layout, fetch at once
// One weight for every gain: a signature is a signature, and shading by size
// implied a ranking the card is not making.
const deltaStyle = () => ({ background: '#059669', color: '#fff' });

const VISIBLE = 3;        // compact panel under the hero
const VISIBLE_CARD = 9;  // full-height card in the layout row

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* True ticker motion. The new row is prepended, so the list starts shifted up
   by exactly one row height and settles to zero — every row slides down one
   step in unison. Only the track is transformed; no row or card dimension
   changes, which is what made earlier attempts jump the card. */
const STEP_MS = 420;

const stepDown = keyframes`
  from { transform: translateY(calc(var(--row-h) * -1)); }
  to   { transform: translateY(0); }
`;

const Track = styled.div`
  /* Row height is fixed in card mode; the panel rows are shorter. The step
     distance must match exactly or the list drifts. */
  --row-h: ${p => (p.$asCard ? '78px' : '52px')};
  ${p => p.$asCard && `
    @media (max-width: 768px) { --row-h: 116px; }
  `}
  will-change: transform;
  ${p => p.$stepping && css`
    animation: ${stepDown} ${STEP_MS}ms cubic-bezier(.33, 0, .2, 1);
  `}
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

// A brief tint so the eye lands on the row that is actually new.
const flash = keyframes`
  0%   { background: rgba(16, 185, 129, 0.20); }
  100% { background: transparent; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
  50%      { box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
`;

const Shell = styled.div`
  position: relative;
  /* Default: a panel under the hero stats. */
  max-width: 430px;
  margin: 14px auto 0;
  border-radius: 13px;
  overflow: hidden;
  background: rgba(22, 22, 40, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.34);
  animation: ${slideUp} 0.34s cubic-bezier(.2, .7, .3, 1);

  /* Card mode must come last — these override the panel defaults above, and
     in CSS the later rule wins regardless of which is more specific. */
  ${p => p.$asCard && `
    max-width: none;
    margin: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.97);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    backdrop-filter: none;
  `}

  @media (prefers-reduced-motion: reduce) { animation: none; }

  @media (max-width: 560px) {
    max-width: none;
    ${p => !p.$asCard && `margin: 12px 4px 0;`}
  }
`;

const Bar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: ${p => (p.$asCard ? '14px 34px 14px 20px' : '10px 34px 10px 12px')};
  border: none;
  background: ${p => (p.$asCard
    ? 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)'
    : 'transparent')};
  color: #fff;
  font-family: inherit;
  font-size: ${p => (p.$asCard ? '0.95em' : '0.84em')};
  font-weight: 700;
  text-align: left;
  cursor: ${p => (p.$asCard ? 'default' : 'pointer')};

  > svg:first-child { width: 15px; height: 15px; color: #34d399; flex-shrink: 0; }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
    flex-shrink: 0;
    animation: ${pulse} 2.2s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  }

  /* Card mode mirrors ColCardHeader: icon, stacked title and sub-line, then
     a badge pushed to the right. */
  .titles { line-height: 1.25; }

  h3 {
    margin: 0;
    color: #fff;
    font-size: 1em;
    font-weight: 700;
    letter-spacing: 0.2px;
  }

  .sub {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.72em;
    font-weight: 500;
    margin-left: 1px;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 3px 10px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
    font-size: 0.75em;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .badge .dot { background: #4ade80; }

  /* Hovering pauses rotation so links do not move under the cursor. Without
     a visible cue that reads as the feed having stalled. */
  .badge.paused {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.22);
    color: rgba(255, 255, 255, 0.82);
  }
  .badge.paused svg {
    width: 11px;
    height: 11px;
    fill: currentColor;
  }

  .chev {
    width: 15px;
    height: 15px;
    margin-left: auto;
    color: rgba(255, 255, 255, 0.55);
    transition: transform 0.2s;
    transform: rotate(${p => (p.$open ? '0deg' : '180deg')});
  }
`;

const Close = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;

  svg { width: 13px; height: 13px; }
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
`;

const Body = styled.div`
  flex: 1;
  max-height: ${p => (p.$asCard ? 'none' : (p.$open ? '280px' : '0'))};
  opacity: ${p => (p.$open ? 1 : 0)};
  transition: max-height 0.3s ease, opacity 0.2s ease;
  overflow: hidden;
  border-top: 1px solid rgba(255, 255, 255, ${p => (p.$open ? '0.09' : '0')});
`;

const Row = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;

  /* Matches MiniPetitionItem in the sibling cards: tint, rounded corners and
     an inset border, in this card's green rather than their rust. */
  &:hover {
    background: ${p => (p.$asCard
      ? 'linear-gradient(135deg, rgba(5,150,105,0.07) 0%, rgba(4,120,87,0.05) 100%)'
      : 'rgba(255,255,255,0.06)')};
    ${p => p.$asCard && `
      border-radius: 12px;
      border-bottom-color: transparent;
      box-shadow: inset 0 0 0 1px rgba(5,150,105,0.18);
    `}
  }
  padding: ${p => (p.$asCard ? '11px 20px' : '9px 12px')};
  border-bottom: 1px solid ${p => (p.$asCard ? '#cbd5e0' : 'rgba(255,255,255,0.05)')};
  ${p => p.$asCard && `
    --row-h: 78px;
    height: 78px;
    overflow: hidden;

    /* The card is full width on mobile but the text column is far narrower,
       so a title that fits two lines on desktop needs three or four. Measured
       on a 390px viewport, 7 of 9 titles were being cut. Grow the row rather
       than the clamp alone — the fixed height is what stops the card
       resizing on every rotation. */
    @media (max-width: 768px) {
      --row-h: 116px;
      height: 116px;
    }
  `}

  &:last-child { border-bottom: none; }

  /* Only the newest row animates. It grows from zero height, so the browser
     pushes every row below it down — the movement the eye can actually
     follow, which a fade on its own never showed. */
  /* Height is never animated — that shrank the card and moved all three.
     The row keeps its full height and its contents slide in instead. */
  ${p => p.$isNew && css`
    animation: ${flash} 900ms ease-out;
  `}

  @media (prefers-reduced-motion: reduce) { animation: none; }

  /* A pill whose strength scales with the gain: +3 and +67 read identically
     as plain text, so big movements were invisible at a glance. */
  .delta {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    ${p => p.$asCard ? `
      min-width: 54px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 9px;
      border-radius: 7px;
      font-size: 0.8em;
      font-weight: 800;
      letter-spacing: -0.2px;
    ` : `
      min-width: 44px;
      text-align: right;
      color: #34d399;
      font-size: 0.86em;
      font-weight: 800;
    `}
  }

  .body {
    flex: 1;
    min-width: 0;
    ${p => p.$asCard && `
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 3px;
    `}
  }

  .title {
    display: block;
    color: ${p => (p.$asCard ? '#1a202c' : 'rgba(255,255,255,0.9)')};
    font-size: ${p => (p.$asCard ? '0.88em' : '0.8em')};
    font-weight: ${p => (p.$asCard ? '600' : '400')};
    line-height: 1.35;
    text-decoration: none;
    /* Wrap to at most two lines in card mode, always reserving the space for
       two. Without a fixed height the card grew and shrank on every rotation
       and the grid resized all three cards along with it. */
    white-space: ${p => (p.$asCard ? 'normal' : 'nowrap')};
    overflow: hidden;
    text-overflow: ellipsis;
    ${p => p.$asCard && `
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;

      @media (max-width: 768px) {
        -webkit-line-clamp: 4;
      }
    `}
  }
  &:hover .title { color: ${p => (p.$asCard ? '#4338ca' : '#fff')}; text-decoration: underline; }

  .total {
    color: ${p => (p.$asCard ? '#94a3b8' : 'rgba(255,255,255,0.4)')};
    font-size: 0.72em;
    margin-top: 0;
    font-variant-numeric: tabular-nums;
  }

  /* Marks a row that is also in the Trending top 10, so the two cards read
     as one story rather than two unrelated lists. Borrows the Trending
     card's rust so the link is obvious at a glance. */
  .trend {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-left: 7px;
    padding: 1px 5px 1px 4px;
    border-radius: 4px;
    font-weight: 700;
    letter-spacing: -0.1px;
    background: ${p => (p.$asCard ? '#fff1e7' : 'rgba(251,146,60,0.18)')};
    color: ${p => (p.$asCard ? '#c2410c' : '#fdba74')};

    svg {
      width: 10px;
      height: 10px;
      stroke-width: 2.5;
    }
  }
`;

const shimmer = keyframes`
  0%   { opacity: 0.45; }
  50%  { opacity: 0.85; }
  100% { opacity: 0.45; }
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 20px;
  height: 78px;
  /* Matches Row's mobile height, or the card resizes when data replaces it. */
  @media (max-width: 768px) { height: 116px; }
  border-bottom: 1px solid #cbd5e0;
  &:last-child { border-bottom: none; }

  .bar {
    height: 10px;
    border-radius: 5px;
    background: #e2e8f0;
    animation: ${shimmer} 1.5s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  }
  .n { width: 34px; flex-shrink: 0; }
  .body { flex: 1; min-width: 0; }
  .t { width: 88%; margin-bottom: 7px; }
  .s { width: 46%; height: 8px; }
`;

const Foot = styled.div`
  margin-top: auto;
  padding: ${p => (p.$asCard ? '11px' : '7px 12px 9px')};
  border-top: ${p => (p.$asCard ? '1px solid #cbd5e0' : 'none')};
  background: ${p => (p.$asCard ? '#f7fafc' : 'transparent')};
  color: ${p => (p.$asCard ? '#718096' : 'rgba(255,255,255,0.35)')};
  font-size: ${p => (p.$asCard ? '0.76em' : '0.68em')};
  font-weight: ${p => (p.$asCard ? '600' : '400')};
  text-align: center;
  line-height: 1.5;

  .key {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    margin-top: 3px;
    white-space: nowrap;
    color: ${p => (p.$asCard ? '#c2410c' : '#fdba74')};

    svg { width: 11px; height: 11px; stroke-width: 2.5; }
  }
`;

/**
 * Live signature ticker, shown under the homepage hero stats. Open state is
 * controlled by the parent so the "Live" stat can toggle it.
 */
const LiveActivity = ({ open = true, onClose, asCard = false, trendingRanks = null }) => {
    const rowCount = asCard ? VISIBLE_CARD : VISIBLE;
    // Rotating rows under the cursor makes links hard to hit, so the ticker
    // holds still while someone is reading or aiming at one.
    const hovering = useRef(false);
    // Mirrors `hovering` in state purely so the badge can show the pause; the
    // ref stays authoritative for the timer, which must not re-run on render.
    const [paused, setPaused] = useState(false);
    const setHover = (v) => { hovering.current = v; setPaused(v); };
    // True only just after a row is inserted, so the initial fill does not
    // animate every row at once.
    const [justAdded, setJustAdded] = useState(false);
    const [shown, setShown] = useState([]);
    const [ready, setReady] = useState(false);
    const queue = useRef([]);
    const seen = useRef(new Set());

    useEffect(() => {
        let cancelled = false;
        let revealTimer = null;
        let fetchTimer = null;
        let appearTimer = null;

        // The batch always arrives in the same order, so some randomness is
        // needed or every visitor sees identical rows. A plain shuffle mostly
        // surfaces +1s — about two thirds of moving petitions gain only one or
        // two signatures per sync — so the draw is weighted by size.
        const weightedShuffle = arr => arr
            .map(e => ({ e, w: Math.random() * Math.sqrt(Math.max(1, e.delta)) }))
            .sort((a, b) => b.w - a.w)
            .map(x => x.e);

        const load = (first = false) => {
            fetch(`${TIMEDATA_BASE}/live/activity?limit=40`)
                .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
                .then(d => {
                    if (cancelled) return;
                    const events = weightedShuffle(d.events || []);
                    if (!events.length) return;
                    if (first) {
                        // Fill straight away: a widget that slides in empty and
                        // populates over ten seconds reads as broken.
                        const head = events.slice(0, rowCount);
                        head.forEach(e => seen.current.add(`${e.petition_id}:${e.at}`));
                        setShown(head);
                        queue.current = events.slice(rowCount);
                        setReady(true);
                        return;
                    }
                    const fresh = events.filter(e => !seen.current.has(`${e.petition_id}:${e.at}`));
                    queue.current = queue.current.concat(fresh);
                })
                .catch(() => { /* decorative — stay hidden rather than erroring */ });
        };

        const reveal = () => {
            if (cancelled) return;
            if (hovering.current) {
                // Try again shortly rather than skipping this turn entirely.
                revealTimer = setTimeout(reveal, 900);
                return;
            }
            const next = queue.current.shift();
            if (next) {
                seen.current.add(`${next.petition_id}:${next.at}`);
                setShown(prev => [next, ...prev].slice(0, rowCount));
                setJustAdded(true);
                setTimeout(() => { if (!cancelled) setJustAdded(false); }, STEP_MS);
            } else {
                // Queue drained before the next sync: rotate what is on screen
                // so the widget keeps moving instead of freezing.
                setShown(prev => (prev.length < 2
                    ? prev
                    : [prev[prev.length - 1], ...prev.slice(0, prev.length - 1)]));
                setJustAdded(true);
                setTimeout(() => { if (!cancelled) setJustAdded(false); }, STEP_MS);
            }
            revealTimer = setTimeout(reveal, nextRevealDelay());
        };

        // Defer the first fetch so it does not compete with page load, but
        // only on mount — reopening must be instant, the data is already here.
        appearTimer = setTimeout(() => {
            load(true);
            revealTimer = setTimeout(reveal, nextRevealDelay());
            fetchTimer = setInterval(() => load(false), REFETCH_MS);
        }, asCard ? APPEAR_DELAY_CARD : APPEAR_DELAY);

        return () => {
            cancelled = true;
            clearTimeout(appearTimer);
            clearTimeout(revealTimer);
            clearInterval(fetchTimer);
        };
    }, [rowCount]);

    if (!open) return null;
    // Returning null while loading collapsed the column and made the whole
    // row jump once data arrived. Hold the space with a skeleton instead.
    const loading = !ready || !shown.length;
    // Remount the track whenever the head row changes, otherwise the CSS
    // animation only ever runs once.
    const stepKey = shown.length ? `${shown[0].petition_id}-${shown[0].at}` : 'empty';
    const showTrendKey = !!trendingRanks
        && shown.some(e => trendingRanks.get(String(e.petition_id)));
    if (loading && !asCard) return null;

    return (
        <Shell
            $asCard={asCard}
            role="complementary"
            aria-label="Live petition signatures"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocusCapture={() => setHover(true)}
            onBlurCapture={() => setHover(false)}
        >
            <Bar as="div" $open $asCard={asCard}>
                <Activity aria-hidden="true" />
                {asCard ? (
                    <>
                        <div className="titles">
                            <h3>Live signatures</h3>
                            <div className="sub">As they land, right now</div>
                        </div>
                        <span className={paused ? 'badge paused' : 'badge'}>
                            {paused ? <Pause aria-hidden="true" /> : <span className="dot" />}
                            {paused ? 'Paused' : 'Live'}
                        </span>
                    </>
                ) : (
                    <>
                        <span className="dot" />
                        Live signatures
                    </>
                )}
            </Bar>
            {!asCard && (
                <Close type="button" onClick={onClose} aria-label="Hide live signatures">
                    <X aria-hidden="true" />
                </Close>
            )}

            <Body $open $asCard={asCard} aria-live="polite" aria-busy={loading}>
                {loading && Array.from({ length: rowCount }, (_, i) => (
                    <SkeletonRow key={`sk-${i}`} aria-hidden="true">
                        <div className="bar n" />
                        <div className="body">
                            <div className="bar t" />
                            <div className="bar s" />
                        </div>
                    </SkeletonRow>
                ))}
                {!loading && (
                <Track $asCard={asCard} $stepping={justAdded} key={stepKey}>
                {shown.map((e, i) => (
                    <Row
                        key={`${e.petition_id}-${e.at}`}
                        to={`/petition/${e.petition_id}/${slugify(e.title)}`}
                        $asCard={asCard}
                        $isNew={i === 0 && justAdded}
                    >
                        <span
                            className="delta"
                            style={asCard ? deltaStyle(e.delta) : undefined}
                        >
                            +{e.delta.toLocaleString('en-GB')}
                        </span>
                        <span className="body">
                            <span className="title">{e.title}</span>
                            <span className="total">
                                {e.signatures.toLocaleString('en-GB')} signatures
                                {trendingRanks && trendingRanks.get(String(e.petition_id)) && (
                                    <span
                                        className="trend"
                                        title={`#${trendingRanks.get(String(e.petition_id))} trending in the last 2 hours`}
                                    >
                                        <TrendingUp aria-hidden="true" />
                                        #{trendingRanks.get(String(e.petition_id))}
                                    </span>
                                )}
                            </span>
                        </span>
                    </Row>
                ))}
                </Track>
                )}
                <Foot $asCard={asCard}>
                    {loading ? 'Loading latest signatures…' : (
                        <>
                            Signatures gained in the last few minutes
                            {/* Only explain the badge when one is actually on
                                screen — the rows rotate, so it often is not. */}
                            {showTrendKey && (
                                <span className="key">
                                    <TrendingUp aria-hidden="true" />
                                    = trending top 10, last 2 hours
                                </span>
                            )}
                        </>
                    )}
                </Foot>
            </Body>
        </Shell>
    );
};

export default LiveActivity;
